/**
 * WooCommerce Pagination Widget JavaScript
 * 100% Vanilla JavaScript - Zero jQuery dependency
 *
 * @package Storedash_Checkout
 * @since 2.8.0
 */

(function() {
    'use strict';

    function extractPageNumber(url) {
        if (!url) return 1;
        const pagedMatch = url.match(/[?&]paged=(\d+)/);
        if (pagedMatch) return parseInt(pagedMatch[1]);
        const pageMatch = url.match(/\/page\/(\d+)/);
        if (pageMatch) return parseInt(pageMatch[1]);
        return 1;
    }

    function getCurrentFilters() {
        const filters = {};
        const params = new URLSearchParams(window.location.search);
        params.forEach((value, key) => {
            if (key.startsWith('filter_')) {
                filters[key.replace('filter_', '')] = value.split(',');
            }
        });
        return filters;
    }

    /**
     * Keep in sync with filters.js and clear-filters.js getContext().
     */
    function getPageContext() {
        const wrapper = document.querySelector('.storedash-products-wrapper[data-page-context]');
        if (wrapper) {
            try {
                const ctx = JSON.parse(wrapper.dataset.pageContext);
                return {
                    is_shop: ctx.is_shop || false,
                    is_category: ctx.is_category || false,
                    is_taxonomy: ctx.is_taxonomy || false,
                    is_search: ctx.is_search || false,
                    category_id: ctx.category_id || 0,
                    taxonomy: ctx.taxonomy || '',
                    term_id: ctx.term_id || 0,
                    search: ctx.search || '',
                };
            } catch (e) { /* fall through */ }
        }

        const body = document.body;
        const params = new URLSearchParams(window.location.search);
        const context = {
            is_shop: body.classList.contains('post-type-archive-product') || body.classList.contains('woocommerce-shop'),
            is_category: body.classList.contains('tax-product_cat'),
            is_taxonomy: body.classList.contains('tax-product_tag') || body.classList.contains('woocommerce-taxonomy'),
            is_search: body.classList.contains('search-results') || params.has('s'),
            category_id: 0,
            taxonomy: '',
            term_id: 0,
            search: params.get('s') || '',
        };

        if (context.is_category) {
            for (const cls of body.className.split(' ')) {
                if (cls.startsWith('term-')) {
                    const id = cls.replace('term-', '');
                    if (!isNaN(id)) {
                        context.category_id = parseInt(id);
                        context.term_id = parseInt(id);
                        context.taxonomy = 'product_cat';
                        break;
                    }
                }
            }
        }

        if (context.is_taxonomy && !context.is_category) {
            for (const cls of body.className.split(' ')) {
                if (cls.startsWith('term-')) {
                    const id = cls.replace('term-', '');
                    if (!isNaN(id)) context.term_id = parseInt(id);
                }
                if (cls.startsWith('tax-')) {
                    context.taxonomy = cls.replace('tax-', '');
                }
            }
        }

        return context;
    }

    function getWidgetSettings() {
        const productGridWidget = document.querySelector('.elementor-widget-storedash-product-grid');
        const productsWrapper = productGridWidget
            ? productGridWidget.querySelector('.storedash-products-wrapper[data-settings]')
            : document.querySelector('.storedash-products-wrapper[data-settings]');

        if (!productsWrapper) return {};
        try { return JSON.parse(productsWrapper.dataset.settings); } catch (e) { return {}; }
    }

    function getCurrentSort(widgetSettings = {}) {
        const urlParams = new URLSearchParams(window.location.search);
        return {
            orderby: urlParams.get('orderby') || widgetSettings.orderby || storedashFiltersData.orderby || 'menu_order',
            order: urlParams.get('order') || widgetSettings.order || storedashFiltersData.order || 'ASC',
        };
    }

    function prefetchNextPage(currentPage, totalPages) {
        if (currentPage >= totalPages) return;
        if (!window.StoredashFilterCache) return;

        const nextPage = currentPage + 1;
        const filters = getCurrentFilters();
        const context = getPageContext();
        const widgetSettings = getWidgetSettings();
        const { orderby, order } = getCurrentSort(widgetSettings);
        const perPage = widgetSettings.products_per_page || storedashFiltersData.perPage || 12;

        const requestParams = {
            filters, context, paged: nextPage, per_page: perPage,
            orderby, order, widget_settings: widgetSettings,
            render: 'html', skip_counts: true,
        };

        if (window.StoredashFilterCache.get(requestParams)) return;

        const doPrefetch = () => {
            fetch(storedashFiltersData.restUrl + 'storedash/v1/ajax-filter', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-WP-Nonce': storedashFiltersData.nonce || '' },
                body: JSON.stringify(requestParams),
            })
            .then(r => r.ok ? r.json() : null)
            .then(data => { if (data && data.success) window.StoredashFilterCache.set(requestParams, data); })
            .catch(() => {});
        };

        if ('requestIdleCallback' in window) {
            requestIdleCallback(doPrefetch, { timeout: 3000 });
        } else {
            setTimeout(doPrefetch, 500);
        }
    }

    async function loadPageViaAJAX(pageNum) {
        const filters = getCurrentFilters();
        const context = getPageContext();
        const widgetSettings = getWidgetSettings();
        const { orderby, order } = getCurrentSort(widgetSettings);
        const perPage = widgetSettings.products_per_page || storedashFiltersData.perPage || 12;

        const requestParams = {
            filters, context, paged: pageNum, per_page: perPage,
            orderby, order, widget_settings: widgetSettings,
            render: 'html', skip_counts: false,
        };

        if (window.StoredashFilterCache) {
            const cached = window.StoredashFilterCache.get(requestParams);
            if (cached && cached.success) {
                handlePageData(cached, pageNum, filters);
                return;
            }
        }

        const productsWrapper = document.querySelector('.storedash-products-wrapper');
        if (productsWrapper) {
            productsWrapper.style.opacity = '0.5';
            productsWrapper.style.pointerEvents = 'none';
        }

        try {
            const response = await fetch(storedashFiltersData.restUrl + 'storedash/v1/ajax-filter', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-WP-Nonce': storedashFiltersData.nonce || '' },
                body: JSON.stringify(requestParams),
            });

            if (!response.ok) throw new Error('AJAX pagination failed');

            const data = await response.json();

            if (data.success && window.StoredashFilterCache) {
                window.StoredashFilterCache.set(requestParams, data);
            }

            if (data.success) {
                handlePageData(data, pageNum, filters);
            }
        } catch (error) {
            const params = new URLSearchParams(window.location.search);
            params.set('paged', pageNum);
            window.location.href = window.location.pathname + '?' + params.toString();
        } finally {
            if (productsWrapper) {
                productsWrapper.style.opacity = '1';
                productsWrapper.style.pointerEvents = '';
            }
        }
    }

    function handlePageData(data, pageNum, filters) {
        const rendererAvailable = typeof window.StoredashProductRenderer !== 'undefined';
        const fallbackHTML = rendererAvailable
            ? window.StoredashProductRenderer.renderProducts(data.products || [], data.widget_settings || {}, data.i18n || {})
            : '';
        const finalHTML = data.products_html || fallbackHTML;

        if (!finalHTML) return;

        const productGridWidget = document.querySelector('.elementor-widget-storedash-product-grid');
        const grid = productGridWidget
            ? productGridWidget.querySelector('.storedash-products-grid, .swiper-wrapper')
            : null;
        const productsWrapper = document.querySelector('.storedash-products-wrapper');

        if (grid) {
            grid.style.transition = 'opacity 150ms';
            grid.style.opacity = '0';
            setTimeout(() => {
                grid.innerHTML = finalHTML;
                grid.style.opacity = '1';
            }, 150);
        }

        updatePaginationDisplay(pageNum, data.total_pages);
        updateURLWithPage(pageNum, filters);

        if (productsWrapper) {
            const offset = productsWrapper.getBoundingClientRect().top + window.scrollY - 100;
            window.scrollTo({ top: offset, behavior: 'smooth' });
        }

        prefetchNextPage(pageNum, data.total_pages || 1);
    }

    function updatePaginationDisplay(currentPage, totalPages) {
        document.querySelectorAll('.pagination-info .current-page').forEach(el => { el.textContent = currentPage; });
        document.querySelectorAll('.pagination-info .max-pages').forEach(el => { el.textContent = totalPages; });

        document.querySelectorAll('.storedash-pagination-wrapper').forEach(el => {
            el.dataset.currentPage = currentPage;
            el.dataset.maxPages = totalPages;
        });

        document.querySelectorAll('.storedash-pagination .page-numbers').forEach(el => {
            el.classList.remove('current');
            if (el.textContent.trim() === currentPage.toString()) {
                const span = document.createElement('span');
                span.className = 'page-numbers current';
                span.textContent = el.textContent.trim();
                el.replaceWith(span);
            }
        });
    }

    function updateURLWithPage(pageNum, filters) {
        const params = new URLSearchParams();
        const currentParams = new URLSearchParams(window.location.search);

        for (const [source, values] of Object.entries(filters)) {
            if (values && values.length > 0) {
                params.set('filter_' + source, values.join(','));
            }
        }

        if (pageNum > 1) params.set('paged', pageNum);

        const searchQuery = currentParams.get('s');
        if (searchQuery) params.set('s', searchQuery);
        const ob = currentParams.get('orderby');
        if (ob) params.set('orderby', ob);
        const ord = currentParams.get('order');
        if (ord) params.set('order', ord);

        const newUrl = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
        window.history.pushState({ filters, page: pageNum, storedashPagination: true }, '', newUrl);
    }

    function interceptPaginationClicks() {
        document.addEventListener('click', function(e) {
            const link = e.target.closest('.storedash-pagination a.page-numbers');
            if (!link) return;
            if (link.classList.contains('current') || link.classList.contains('load-more-button')) return;

            e.preventDefault();
            const pageNum = extractPageNumber(link.getAttribute('href'));
            if (pageNum) loadPageViaAJAX(pageNum);
        });
    }

    class StoredashPagination {
        constructor(element) {
            this.element = element;

            const wrapper = element.classList.contains('storedash-pagination-wrapper')
                ? element
                : element.querySelector('.storedash-pagination-wrapper');

            if (!wrapper) return;

            this.wrapper = wrapper;
            this.pagination = wrapper.querySelector('.storedash-pagination');
            this.type = wrapper.dataset.type;
            this.settings = {};
            try { this.settings = JSON.parse(wrapper.dataset.settings) || {}; } catch (e) { /* noop */ }
            this.currentPage = parseInt(wrapper.dataset.currentPage) || 1;
            this.maxPages = parseInt(wrapper.dataset.maxPages) || 1;
            this.loading = false;
            this.observer = null;
            this._clickHandler = null;

            this.init();
        }

        init() {
            if (this.type === 'load_more') {
                this.initLoadMore();
            } else if (this.type === 'infinite_scroll') {
                this.initInfiniteScroll();
            }
        }

        initLoadMore() {
            this._clickHandler = (e) => {
                const button = e.target.closest('.load-more-button');
                if (button) {
                    e.preventDefault();
                    this.loadMore();
                }
            };
            this.wrapper.addEventListener('click', this._clickHandler);
        }

        initInfiniteScroll() {
            const trigger = this.wrapper.querySelector('.infinite-scroll-trigger');

            if (!trigger || !('IntersectionObserver' in window)) {
                this.type = 'load_more';
                this.initLoadMore();
                return;
            }

            this.observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting && !this.loading && this.currentPage < this.maxPages) {
                        this.loadMore();
                    }
                });
            }, {
                root: null,
                rootMargin: `${this.settings.infiniteScrollOffset || 300}px`,
                threshold: 0
            });

            this.observer.observe(trigger);
        }

        async loadMore() {
            // Sync from dataset first — the document-level filters:changed
            // listener resets page/totals on the wrapper dataset (it has no
            // reference to this instance), so instance state may be stale.
            this.currentPage = parseInt(this.wrapper.dataset.currentPage) || this.currentPage;
            this.maxPages = parseInt(this.wrapper.dataset.maxPages) || this.maxPages;

            if (this.loading || this.currentPage >= this.maxPages) return;

            this.loading = true;
            const nextPage = this.currentPage + 1;

            const button = this.wrapper.querySelector('.load-more-button');
            if (button) button.classList.add('loading');

            try {
                const filters = getCurrentFilters();
                const context = getPageContext();
                const widgetSettings = getWidgetSettings();
                const { orderby, order } = getCurrentSort(widgetSettings);

                const response = await fetch(storedashFiltersData.restUrl + 'storedash/v1/ajax-filter', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'X-WP-Nonce': storedashFiltersData.nonce || '' },
                    body: JSON.stringify({
                        filters, context, paged: nextPage,
                        per_page: widgetSettings.products_per_page || storedashFiltersData.perPage || 12,
                        orderby, order, widget_settings: widgetSettings, render: 'html',
                    })
                });

                if (!response.ok) throw new Error('Load more request failed');

                const data = await response.json();

                if (data.success) {
                    const rendererAvailable = typeof window.StoredashProductRenderer !== 'undefined';
                    const fallbackHTML = rendererAvailable
                        ? window.StoredashProductRenderer.renderProducts(data.products || [], data.widget_settings || {}, data.i18n || {})
                        : '';
                    const finalHTML = data.products_html || fallbackHTML;

                    if (finalHTML) {
                        this.onLoadSuccess(finalHTML, nextPage, data.total_pages);
                    } else {
                        this.onLoadError();
                    }
                } else {
                    this.onLoadError();
                }
            } catch (error) {
                this.onLoadError();
            }
        }

        onLoadSuccess(productsHTML, nextPage, totalPages) {
            const productGridWidget = document.querySelector('.elementor-widget-storedash-product-grid');
            const grid = productGridWidget
                ? productGridWidget.querySelector('.storedash-products-grid, .swiper-wrapper')
                : null;

            if (!grid) {
                this.onLoadError();
                return;
            }

            const temp = document.createElement('div');
            temp.innerHTML = productsHTML;

            // Dedup safety net: never append a product card already in the grid.
            // load_more/infinite_scroll APPEND (unlike numbered pagination which
            // replaces innerHTML), so any residual server-side ordering drift between
            // page 1 and this page would otherwise render a visible duplicate. We key
            // off data-product-id (present on every card — server- and JS-rendered).
            const cardId = (el) => {
                if (!el || !el.getAttribute) return null;
                if (el.matches && el.matches('[data-product-id]')) return el.getAttribute('data-product-id');
                const inner = el.querySelector && el.querySelector('[data-product-id]');
                return inner ? inner.getAttribute('data-product-id') : null;
            };
            const existingIds = new Set(
                Array.from(grid.querySelectorAll('[data-product-id]'))
                    .map(el => el.getAttribute('data-product-id'))
                    .filter(Boolean)
            );
            const newProducts = Array.from(temp.children).filter(el => {
                const id = cardId(el);
                if (id === null) return true; // keep non-card nodes (separators, etc.)
                if (existingIds.has(id)) return false; // already shown — drop the duplicate
                existingIds.add(id);
                return true;
            });

            if (newProducts.length === 0) {
                // Everything returned was already on the page — advance state, append nothing.
                this.currentPage = nextPage;
                this.maxPages = totalPages || this.maxPages;
                this.updateUI();
                this.loading = false;
                return;
            }

            newProducts.forEach(el => {
                el.style.opacity = '0';
                el.style.transform = 'translateY(20px)';
            });

            grid.append(...newProducts);

            setTimeout(() => {
                newProducts.forEach(el => {
                    el.style.transition = 'opacity 0.4s ease, transform 0.4s ease';
                    el.style.opacity = '1';
                    el.style.transform = 'translateY(0)';
                });
            }, 100);

            this.currentPage = nextPage;
            this.maxPages = totalPages || this.maxPages;
            this.updateUI();

            document.dispatchEvent(new CustomEvent('storedash:products_loaded', {
                detail: { newProducts }, bubbles: true
            }));
            document.body.dispatchEvent(new Event('wc-product-gallery-after-init', { bubbles: true }));

            this.loading = false;
        }

        onLoadError() {
            this.loading = false;
            const button = this.wrapper.querySelector('.load-more-button');
            if (!button) return;
            button.classList.remove('loading');

            const textEl = button.querySelector('.load-more-text');
            if (textEl) {
                const original = textEl.textContent;
                textEl.textContent = 'Error loading products';
                setTimeout(() => { textEl.textContent = original; }, 3000);
            }
        }

        updateUI() {
            const currentPageSpan = this.wrapper.querySelector('.current-page');
            if (currentPageSpan) currentPageSpan.textContent = this.currentPage;

            const button = this.wrapper.querySelector('.load-more-button');
            if (button) button.classList.remove('loading');

            if (this.currentPage >= this.maxPages) {
                if (this.type === 'infinite_scroll' && this.observer) {
                    this.observer.disconnect();
                }
                if (button) {
                    // Hide (don't replace) so a later filter/sort change with
                    // more pages can restore the button via the
                    // filters:changed listener.
                    button.style.display = 'none';
                    if (!this.wrapper.querySelector('.pagination-message')) {
                        const message = document.createElement('div');
                        message.className = 'pagination-message';
                        message.textContent = this.settings.noMoreText;
                        button.insertAdjacentElement('afterend', message);
                    }
                }
            } else if (button) {
                button.dataset.nextPage = this.currentPage + 1;
            }

            this.wrapper.dataset.currentPage = this.currentPage;
            this.wrapper.dataset.maxPages = this.maxPages;
        }

        destroy() {
            if (this.observer) this.observer.disconnect();
            if (this._clickHandler) this.wrapper.removeEventListener('click', this._clickHandler);
        }
    }

    // Intercept pagination clicks (document-level delegation — works immediately)
    interceptPaginationClicks();

    // Handle filter changes — reset pagination to page 1
    document.addEventListener('storedash:filters:changed', function(event) {
        const detail = event.detail || {};

        // The sort widget dispatches a pre-AJAX notification (fromSort) that
        // carries no totals — the product grid handles the actual sort and
        // follows up with a fromSortComplete event that does carry totals.
        // Recomputing here would default totalPages to 1 and hide load-more.
        if (detail.fromSort && !detail.fromSortComplete) return;

        // State-sync events without any totals info (e.g. minimal "cleared"
        // notifications) can't tell us page counts — don't guess. Guessing 1
        // hides the load-more button; a follow-up event with real totals
        // always arrives from the AJAX response.
        if (detail.totalPages === undefined && detail.totalProducts === undefined) return;

        // Respect the page the dispatcher reports (client-side sort keeps the
        // already-loaded pages in place); filter changes always send 1.
        const currentPage = parseInt(detail.currentPage) || 1;

        document.querySelectorAll('.storedash-pagination-wrapper').forEach(wrapper => {
            const button = wrapper.querySelector('.load-more-button');
            const pageInfo = wrapper.querySelector('.pagination-info');

            wrapper.dataset.currentPage = currentPage;

            if (pageInfo) {
                const cp = pageInfo.querySelector('.current-page');
                if (cp) cp.textContent = String(currentPage);
            }

            let totalPages = 1;
            if (detail.totalPages) {
                totalPages = parseInt(detail.totalPages);
            } else if (detail.totalProducts && detail.perPage) {
                totalPages = Math.ceil(detail.totalProducts / detail.perPage);
            } else if (detail.totalProducts && detail.per_page) {
                totalPages = Math.ceil(detail.totalProducts / detail.per_page);
            }

            wrapper.dataset.maxPages = totalPages;

            if (pageInfo) {
                const mp = pageInfo.querySelector('.max-pages');
                if (mp) mp.textContent = totalPages;
            }

            if (button) {
                button.dataset.nextPage = currentPage + 1;

                if (detail.totalProducts === 0) {
                    button.style.display = 'none';
                    if (!wrapper.querySelector('.pagination-message')) {
                        wrapper.insertAdjacentHTML('beforeend', '<div class="pagination-message">No products match your filters.</div>');
                    }
                } else {
                    button.style.display = totalPages <= currentPage ? 'none' : '';
                    const msg = wrapper.querySelector('.pagination-message');
                    if (msg) msg.remove();
                }
            }

            const prevArrow = wrapper.querySelector('.page-numbers.prev');
            const nextArrow = wrapper.querySelector('.page-numbers.next');
            const pageNumbers = wrapper.querySelectorAll('.page-numbers:not(.prev):not(.next)');

            if (totalPages <= 1) {
                if (prevArrow) prevArrow.style.display = 'none';
                if (nextArrow) nextArrow.style.display = 'none';
                pageNumbers.forEach(el => { el.style.display = 'none'; });
            } else {
                pageNumbers.forEach(el => { el.style.display = ''; });
                if (prevArrow) prevArrow.style.display = 'none';
                if (nextArrow) nextArrow.style.display = totalPages > 1 ? '' : 'none';
            }
        });
    });

    // Elementor editor: re-init when widget is added/modified in live editor
    if (typeof elementorFrontend !== 'undefined' && elementorFrontend.hooks) {
        elementorFrontend.hooks.addAction('frontend/element_ready/storedash-pagination.default', function($scope) {
            const element = $scope instanceof Element ? $scope : $scope[0];
            if (!element._storedashPagInit) {
                element._storedashPagInit = true;
                new StoredashPagination(element);
            }
        });
    }

    // Frontend: always init on DOMContentLoaded (works logged-in and logged-out)
    function initPagination() {
        document.querySelectorAll('.elementor-widget-storedash-pagination, .brxe-storedash-pagination').forEach(el => {
            if (!el._storedashPagInit) {
                el._storedashPagInit = true;
                new StoredashPagination(el);
            }
        });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initPagination);
    } else {
        initPagination();
    }

    // Bricks Builder editor integration
    window.storedashBricksPagination = function(element) {
        if (!element) return;
        if (!element._storedashPagInit && element.querySelector('.storedash-pagination-wrapper')) {
            element._storedashPagInit = true;
            new StoredashPagination(element);
        }
    };

})();
